import {
	createFileRoute,
	Link,
	useNavigate,
	useSearch,
} from "@tanstack/react-router"
import { Edit, FolderTree, Info } from "lucide-react"
import {
	ENTITY_PAGE_CLASS,
	EntityDetailSkeleton,
	EntityDetailSplit,
	StagingMetadataPanel,
	SummaryGroup,
	SummaryPanel,
	SummaryRow,
} from "@/components/catalogue-entity/entity-detail-layout"
import {
	DetailPanel,
	DetailPanelSection,
	KeyValueItem,
	KeyValueList,
	PageHeader,
} from "@/components/layout"
import { Badge, type BadgeProps } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { createTabSearch } from "@/hooks/use-url-tab"
import { useStagingExpansionGroup } from "@/queries/expansion-groups/use-staging-expansion-group"
import type { StagingExpansionGroupResponse } from "@/types/expansion-groups.types"
import { formatGameName } from "@/utils/game.helpers"

const TABS = ["details", "metadata"] as const
type ExpansionGroupTab = (typeof TABS)[number]

export const Route = createFileRoute("/expansion-groups/$id/")({
	component: ExpansionGroupDetailPage,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			...createTabSearch(TABS)(search),
			gameSlug:
				typeof search.gameSlug === "string" ? search.gameSlug : undefined,
		}
	},
})

function ExpansionGroupDetailPage() {
	const { id } = Route.useParams()
	const searchParams = useSearch({ from: "/expansion-groups/$id/" })
	const navigate = useNavigate()

	const activeTab = searchParams.tab ?? "details"
	const setActiveTab = (tab: string) => {
		navigate({
			to: "/expansion-groups/$id",
			params: { id },
			search:
				tab === "details"
					? { gameSlug: searchParams.gameSlug }
					: {
							gameSlug: searchParams.gameSlug,
							tab: tab as ExpansionGroupTab,
						},
			replace: true,
		})
	}

	const { expansionGroup, isLoading, error } = useStagingExpansionGroup({
		expansionGroupSlug: id,
		gameSlug: searchParams.gameSlug || "",
	})

	if (isLoading) {
		return <EntityDetailSkeleton />
	}

	if (error || !expansionGroup) {
		return (
			<div className="p-4 sm:p-6">
				<PageHeader
					title="Expansion group not found"
					description={
						error?.message ||
						`The expansion group with ID ${id} could not be found.`
					}
				/>
			</div>
		)
	}

	const groupData = expansionGroup.data as StagingExpansionGroupResponse["data"]
	const gameSlug = expansionGroup.gameSlug
	const gameName = formatGameName(gameSlug)

	return (
		<div className={ENTITY_PAGE_CLASS}>
			<PageHeader
				title={groupData.name}
				description={
					<span className="flex flex-wrap items-center gap-x-2">
						<span className="font-mono text-foreground">
							{expansionGroup.entitySlug}
						</span>
						<span className="text-meta">·</span>
						<span>{`Expansion group for ${gameName}`}</span>
					</span>
				}
				actions={
					<Button asChild>
						<Link
							to="/expansion-groups/$id/manage"
							params={{ id }}
							search={{ gameSlug }}
						>
							<Edit />
							Edit
						</Link>
					</Button>
				}
			/>

			<EntityDetailSplit
				aside={
					<SummaryPanel title="Information" icon={Info}>
						<SummaryGroup>
							<SummaryRow label="Game">
								<Badge variant={gameSlug as BadgeProps["variant"]}>
									{gameName}
								</Badge>
							</SummaryRow>
							<SummaryRow label="Group code">
								<span className="font-mono">{groupData.code}</span>
							</SummaryRow>
							<SummaryRow label="Slug">
								<span className="font-mono">{expansionGroup.entitySlug}</span>
							</SummaryRow>
							<SummaryRow label="Last updated">
								{new Date(
									expansionGroup._staging.lastModified,
								).toLocaleDateString()}
							</SummaryRow>
						</SummaryGroup>
					</SummaryPanel>
				}
			>
				<Tabs value={activeTab} onValueChange={setActiveTab} className="gap-6">
					<TabsList variant="line">
						<TabsTrigger value="details">Details</TabsTrigger>
						<TabsTrigger value="metadata">Metadata</TabsTrigger>
					</TabsList>

					<TabsContent value="details">
						<DetailPanel title="Group details" icon={FolderTree}>
							<DetailPanelSection>
								<KeyValueList>
									<KeyValueItem label="Identifier" className="font-mono">
										{expansionGroup.entitySlug}
									</KeyValueItem>
									<KeyValueItem label="Entity type">
										{expansionGroup.entityType}
									</KeyValueItem>
									{groupData.code && (
										<KeyValueItem label="Group code" className="font-mono">
											{groupData.code}
										</KeyValueItem>
									)}
									{groupData.logo && (
										<KeyValueItem
											label="Logo URL"
											className="font-mono break-all"
										>
											{groupData.logo}
										</KeyValueItem>
									)}
								</KeyValueList>
							</DetailPanelSection>
						</DetailPanel>
					</TabsContent>

					<TabsContent value="metadata">
						<StagingMetadataPanel staging={expansionGroup._staging} />
					</TabsContent>
				</Tabs>
			</EntityDetailSplit>
		</div>
	)
}
